import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ScrollText, Download, Search, ShieldCheck, ShieldAlert, RefreshCw,
  Calendar as CalendarIcon, Filter, TrendingUp, ArrowUpRight,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const fmtDate = (iso) => {
  if (!iso) return "";
  const [d, t] = String(iso).split("T");
  const [y, mo, da] = (d || "").split("-");
  return `${da}.${mo}.${y}${t ? " · " + t.slice(0, 5) : ""}`;
};
const fmtEUR = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const fmtRate = (r) => `${(Number(r || 0) * 100).toFixed(1).replace(".", ",")} %`;

const PAY_LABELS = { bar: "Bar", karte: "Karte", gutschein: "Gutschein", einladung: "Einladung", frei: "Kostenlos", mitglied: "Mitglied" };

export default function Kassennachschau() {
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today());
  const [payment, setPayment] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ orders: [], count: 0, summary: {} });
  const [tseStatus, setTseStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo)   params.set("date_to", dateTo);
      if (payment)  params.set("payment_method", payment);
      const [{ data: d }, { data: t }] = await Promise.all([
        api.get(`/admin/kassennachschau?${params}`),
        api.get("/admin/tse/status").catch(() => ({ data: null })),
      ]);
      setData(d);
      setTseStatus(t);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return data.orders || [];
    const s = q.trim().toLowerCase();
    return (data.orders || []).filter((o) =>
      (o.order_number || "").toLowerCase().includes(s) ||
      (o.guest_name || "").toLowerCase().includes(s) ||
      (o.movie_title || "").toLowerCase().includes(s) ||
      (o.tse_signature || "").toLowerCase().includes(s) ||
      String(o.tse_counter || "").includes(s)
    );
  }, [data.orders, q]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo)   params.set("date_to", dateTo);
    const token = localStorage.getItem("mh_token") || "";
    // Direct-Download via Blob-Auth
    api.get(`/admin/kassennachschau/export.csv?${params}`, { responseType: "blob" })
      .then(({ data: blob }) => {
        const url = URL.createObjectURL(new Blob([blob], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `kassennachschau_${dateFrom || "all"}_${dateTo || "all"}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("CSV-Export gestartet");
      })
      .catch(() => toast.error("Export fehlgeschlagen"));
    void token;
  };

  const summary = data.summary || {};

  return (
    <div className="space-y-6" data-testid="kassennachschau-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ScrollText size={22} className="text-[#D4AF37]" />
          <div>
            <h1 className="font-heading text-3xl">Kassennachschau</h1>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">
              MwSt-Aufschlüsselung · TSE-Signatur · DATEV-Export · Read-Only
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} data-testid="kassennachschau-reload"
            className="inline-flex items-center gap-2 px-3 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Aktualisieren
          </button>
          <button onClick={exportCsv} data-testid="kassennachschau-export"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[11px] uppercase tracking-widest">
            <Download size={12} /> DATEV-CSV
          </button>
          <label className="inline-flex items-center gap-2">
            <input type="month" value={(dateFrom || "").slice(0, 7)}
              onChange={(e) => { const m = e.target.value; if (m) { setDateFrom(`${m}-01`); const [y, mo] = m.split("-"); const last = new Date(Number(y), Number(mo), 0).getDate(); setDateTo(`${m}-${String(last).padStart(2, "0")}`); } }}
              data-testid="kassennachschau-month-picker"
              className="bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-2 text-[11px] font-mono" />
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/datev/export?month=${(dateFrom || "").slice(0, 7)}`}
              target="_blank" rel="noreferrer"
              data-testid="kassennachschau-datev-zip"
              title="Monats-Kassenarchiv als ZIP inkl. index.xml herunterladen"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#EEDC82]/60 text-[#EEDC82] hover:bg-[#8B7328]/20 rounded text-[11px] uppercase tracking-widest">
              <Download size={12} /> DATEV-ZIP
            </a>
          </label>
        </div>
      </div>

      {/* TSE-Status */}
      {tseStatus && (
        <div className="flex items-center gap-4 p-4 rounded-lg border border-[#8FBC8F]/25 bg-[#0F1F0F]/40" data-testid="kassennachschau-tse-status">
          <ShieldCheck size={22} className="text-[#8FBC8F]" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#8FBC8F]">TSE-Lite aktiv · HMAC-SHA256</div>
            <div className="text-sm text-[#F5F0E6] mt-0.5">
              Belegzähler: <b className="font-mono">{tseStatus.counter}</b> · Aktiviert: <span className="font-mono text-xs text-[#A8A398]">{fmtDate(tseStatus.activated_at)}</span>
            </div>
            <div className="text-[10px] text-[#575148] mt-1 italic">{tseStatus.note}</div>
          </div>
          {summary.tse_gap_count > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 border border-[#EE9CA1]/40 rounded text-[10px] uppercase tracking-widest text-[#EE9CA1]" data-testid="kassennachschau-tse-gap">
              <ShieldAlert size={12} /> {summary.tse_gap_count} Lücke(n) · erste ab #{summary.tse_first_gap}
            </div>
          )}
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap items-end gap-3 p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><CalendarIcon size={11} /> Von</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="kassennachschau-date-from"
            className="mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Bis</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="kassennachschau-date-to"
            className="mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><Filter size={11} /> Zahlart</label>
          <select value={payment} onChange={(e) => setPayment(e.target.value)} data-testid="kassennachschau-payment"
            className="mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
            <option value="">— Alle —</option>
            {Object.entries(PAY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><Search size={11} /> Suche</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="kassennachschau-search"
            placeholder="Belegnr · Gast · Film · TSE-Nr · Signatur"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
        </div>
        <button onClick={load} className="px-4 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded text-[11px] uppercase tracking-widest hover:bg-[#D4AF37]/10">
          Anwenden
        </button>
      </div>

      {/* KPI-Kacheln */}
      <div className="grid md:grid-cols-4 gap-4" data-testid="kassennachschau-kpis">
        <KPI label="Belege" value={data.count} sub={`${filtered.length} sichtbar`} icon={<ScrollText size={14} />} />
        <KPI label="Brutto (Umsatz)" value={fmtEUR(summary.gross)} sub={`Netto ${fmtEUR(summary.net)}`} icon={<TrendingUp size={14} />} />
        <KPI label="MwSt-Betrag" value={fmtEUR(summary.vat)} sub={`Regelsatz ${fmtRate(summary.default_rate)}`} icon={<ArrowUpRight size={14} />} />
        <KPI label="Unsignierte Belege" value={summary.unsigned_count || 0} sub={(summary.unsigned_count || 0) === 0 ? "Alle TSE-signiert" : "Historische Belege ohne TSE"} icon={<ShieldCheck size={14} />} accent={(summary.unsigned_count || 0) === 0} />
      </div>

      {/* Zahlarten-Übersicht */}
      {summary.by_payment && Object.keys(summary.by_payment).length > 0 && (
        <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">Umsatz je Zahlart</div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.by_payment).map(([pay, sum]) => (
              <div key={pay} className="px-3 py-2 border border-[#2A2A2A] rounded" data-testid={`kassennachschau-pay-${pay}`}>
                <div className="text-[9px] uppercase tracking-widest text-[#575148]">{PAY_LABELS[pay] || pay}</div>
                <div className="font-heading text-[#D4AF37] mt-0.5">{fmtEUR(sum)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Belege-Tabelle */}
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1A1A1A] text-[10px] uppercase tracking-widest text-[#575148]">
                <th className="text-left px-4 py-3">Datum</th>
                <th className="text-left px-4 py-3">Belegnr</th>
                <th className="text-left px-4 py-3">Gast / Film</th>
                <th className="text-left px-4 py-3">Zahlart</th>
                <th className="text-right px-4 py-3">Brutto</th>
                <th className="text-right px-4 py-3">Netto</th>
                <th className="text-right px-4 py-3">MwSt</th>
                <th className="text-right px-4 py-3">Satz</th>
                <th className="text-left px-4 py-3">TSE</th>
              </tr>
            </thead>
            <tbody data-testid="kassennachschau-rows">
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-16 text-[#575148]">Keine Belege im gewählten Zeitraum.</td></tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} data-testid={`kassennachschau-row-${o.order_number}`}
                    className="border-b border-[#1A1A1A] hover:bg-[#D4AF37]/[0.03]">
                  <td className="px-4 py-2.5 text-xs font-mono text-[#A8A398]">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-2.5 font-mono text-[#EEDC82]">{o.order_number || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-[#F5F0E6]">{o.guest_name || "—"}</div>
                    {o.movie_title && <div className="text-[10px] text-[#575148]">{o.movie_title}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-[#A8A398]">{PAY_LABELS[o.payment_method] || o.payment_method || "—"}</td>
                  <td className="px-4 py-2.5 text-right text-[#F5F0E6] font-mono">{fmtEUR(o.gross)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-[#A8A398]">{fmtEUR(o.net)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-[#EEDC82]">{fmtEUR(o.vat)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-[#575148] text-xs">{fmtRate(o.rate)}</td>
                  <td className="px-4 py-2.5">
                    {o.tse_signed ? (
                      <div className="inline-flex items-center gap-1.5 font-mono text-[10px]">
                        <ShieldCheck size={11} className="text-[#8FBC8F]" />
                        <span className="text-[#8FBC8F]">#{o.tse_counter}</span>
                        <span className="text-[#575148]">·</span>
                        <span className="text-[#A8A398]">{o.tse_signature}</span>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#EE9CA1]">
                        <ShieldAlert size={10} /> Ohne TSE
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#D4AF37]/40 bg-[#D4AF37]/5 font-heading">
                  <td colSpan={4} className="px-4 py-3 text-[10px] uppercase tracking-widest text-[#D4AF37]">Summe · {filtered.length} Belege</td>
                  <td className="px-4 py-3 text-right font-mono text-[#D4AF37]">{fmtEUR(filtered.reduce((s, o) => s + Number(o.gross || 0), 0))}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#A8A398]">{fmtEUR(filtered.reduce((s, o) => s + Number(o.net || 0), 0))}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#EEDC82]">{fmtEUR(filtered.reduce((s, o) => s + Number(o.vat || 0), 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="text-[10px] text-[#575148] italic border-t border-[#1A1A1A] pt-3">
        <b>Hinweis:</b> Diese Ansicht ist read-only für Kassennachschau (§ 146a AO / GoBD). Die TSE-Lite ist eine
        Software-Signatur (HMAC-SHA256) — für einen zertifizierten Belegs-Nachweis nach KassenSichV wird zusätzlich eine
        BSI-zertifizierte Hardware-TSE benötigt.
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon, accent }) {
  return (
    <div className={`p-4 rounded-lg border ${accent ? "border-[#8FBC8F]/30 bg-[#0F1F0F]/30" : "border-[#1A1A1A] bg-[#0A0A0A]"}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">
        {icon} {label}
      </div>
      <div className={`font-heading text-2xl mt-2 ${accent ? "text-[#8FBC8F]" : "text-[#D4AF37]"}`}>{value}</div>
      <div className="text-[10px] text-[#575148] mt-1">{sub}</div>
    </div>
  );
}
